import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Mail, Lock, User, UtensilsCrossed } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

export default function Login() {
  const navigate = useNavigate();
  const { login, register } = useAuth();
  const [mode, setMode] = useState("login");
  const [form, setForm] = useState({ email: "", password: "", name: "" });
  const [busy, setBusy] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      if (mode === "login") {
        await login(form.email, form.password);
        toast.success("Welcome back! 🎩");
      } else {
        await register(form.email, form.password, form.name);
        toast.success("Account created! 🎉");
      }
      navigate("/");
    } catch (err) {
      const detail = err.response?.data?.detail;
      const msg = Array.isArray(detail) ? detail[0]?.msg : detail;
      toast.error(msg || (err.response ? "Invalid credentials." : "Network error. Please try again."));
    } finally {
      setBusy(false);
    }
  };

  const googleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      {/* Brand / hero side */}
      <div className="relative hidden lg:flex flex-col justify-between p-12 overflow-hidden bg-emerald-500">
        <div className="relative z-10 flex items-center">
          <img
            src="https://customer-assets-rejwkqb3.emergentagent.net/job_lunch-web/artifacts/sqvp4z9p_Picture1.jpg"
            alt="Take a break, we got lunch by WAJ"
            className="h-24 w-auto object-contain bg-white rounded-2xl p-2 shadow-lg"
          />
        </div>
        <img
          src="https://images.unsplash.com/photo-1628191139344-11bec156abdc?crop=entropy&cs=srgb&fm=jpg&w=1000&q=80"
          alt="Happy kids eating lunch"
          className="absolute inset-0 w-full h-full object-cover opacity-30"
        />
        <div className="relative z-10 text-white max-w-md">
          <h1 className="font-display font-bold text-4xl leading-tight tracking-tight">
            Yummy lunches, happy kids. 🥪
          </h1>
          <p className="mt-4 text-emerald-50 text-lg font-medium">
            Order school lunches, build custom boxes, plan the whole week — and
            <span className="font-bold"> buy 4, get the 5th free!</span>
          </p>
        </div>
      </div>

      {/* Form side */}
      <div className="flex items-center justify-center p-6 sm:p-12">
        <div className="w-full max-w-md">
          <div className="lg:hidden flex items-center justify-center mb-6">
            <img
              src="https://customer-assets-rejwkqb3.emergentagent.net/job_lunch-web/artifacts/sqvp4z9p_Picture1.jpg"
              alt="Take a break, we got lunch by WAJ"
              className="h-20 w-auto object-contain"
            />
          </div>

          <div className="bg-white rounded-3xl border-2 border-emerald-100 shadow-[0_12px_40px_-10px_rgba(16,185,129,0.2)] p-7 sm:p-9 animate-float-up">
            <div className="flex items-center gap-3 mb-6">
              <div className="h-12 w-12 rounded-2xl bg-emerald-100 text-emerald-600 flex items-center justify-center">
                <UtensilsCrossed size={24} />
              </div>
              <div>
                <h2 className="font-display font-bold text-2xl text-slate-800">
                  {mode === "login" ? "Welcome back!" : "Create account"}
                </h2>
                <p className="text-sm text-slate-500">
                  {mode === "login" ? "Log in to order lunch" : "Join in a few seconds"}
                </p>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-2 bg-slate-100 p-1 rounded-2xl mb-6">
              {["login", "register"].map((m) => (
                <button
                  key={m}
                  data-testid={`auth-tab-${m}`}
                  onClick={() => setMode(m)}
                  className={`py-2 rounded-xl font-display font-semibold text-sm transition-all ${
                    mode === m ? "bg-white text-emerald-600 shadow" : "text-slate-500"
                  }`}
                >
                  {m === "login" ? "Log In" : "Sign Up"}
                </button>
              ))}
            </div>

            <button
              data-testid="google-auth-button"
              onClick={googleLogin}
              className="w-full flex items-center justify-center gap-3 border-2 border-slate-200 hover:border-emerald-300 hover:bg-emerald-50/50 rounded-2xl py-3 font-semibold text-slate-700 transition-all"
            >
              <img src="https://www.gstatic.com/firebasejs/ui/2.0.0/images/auth/google.svg" alt="" className="h-5 w-5" />
              Continue with Google
            </button>

            <div className="flex items-center gap-3 my-5">
              <div className="flex-1 h-px bg-slate-200" />
              <span className="text-xs font-semibold text-slate-400 uppercase">or</span>
              <div className="flex-1 h-px bg-slate-200" />
            </div>

            <form onSubmit={submit} className="space-y-4">
              {mode === "register" && (
                <Field icon={User} testid="auth-name-input" placeholder="Kid's parent name" value={form.name} onChange={set("name")} required />
              )}
              <Field icon={Mail} testid="auth-email-input" type="email" placeholder="you@email.com" value={form.email} onChange={set("email")} required />
              <Field icon={Lock} testid="auth-password-input" type="password" placeholder="Password" value={form.password} onChange={set("password")} required />

              <button
                type="submit"
                data-testid="auth-submit-button"
                disabled={busy}
                className="w-full bg-emerald-500 hover:bg-emerald-600 disabled:opacity-60 text-white font-display font-bold text-lg rounded-2xl py-3.5 shadow-md shadow-emerald-200 transition-all active:scale-[0.98]"
              >
                {busy ? "Please wait…" : mode === "login" ? "Log In" : "Create Account"}
              </button>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
}

const Field = ({ icon: Icon, testid, ...props }) => (
  <div className="relative">
    <Icon size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" />
    <input
      data-testid={testid}
      {...props}
      className="w-full pl-11 pr-4 py-3 rounded-2xl border-2 border-slate-200 focus:border-emerald-400 focus:ring-2 focus:ring-emerald-100 outline-none font-medium text-slate-700 placeholder:text-slate-400 transition-all"
    />
  </div>
);
